import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import sanitizeHtml from 'sanitize-html'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import Blog, { BlogProps } from '@/features/blogs/pages/Blog'
import Error4xx from '@/features/errors/pages/Error4xx'
import Error5xx from '@/features/errors/pages/Error5xx'
import { baseURL } from '@/features/blogs/api'
import requests from '@/lib/requests'
import env from '@/lib/env'
import { BLOG_LOCALES, DEFAULT_LOCALE } from '@/lib/localization/constants'

const sanitizeConfig = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
}

type GetServerSideProps = Partial<BlogProps & GetServerSideCommonProps>

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<GetServerSideProps>> {
  const { query, locale = DEFAULT_LOCALE } = ctx

  let statusCode = 200

  if (!BLOG_LOCALES.includes(locale)) {
    return {
      props: {
        statusCode: 404,
        ...(await serverSideTranslations(locale, ['common'])),
      },
      notFound: true,
    }
  }

  try {
    const response = await requests.get(`${baseURL}/:slug`, {
      params: {
        slug: query.slug,
      },
      headers: {
        'x-api-key': env.PDPA_BLOG_API_KEY,
      },
    })

    return {
      props: {
        statusCode,
        blog: {
          ...response.data.data,
          content: sanitizeHtml(response.data.data.content, sanitizeConfig),
        },
        ...(await serverSideTranslations(locale, ['common'])),
      },
    }
  } catch (error: any) {
    if (error.response) {
      statusCode = error.response.status
    } else {
      statusCode = 500
    }
  }

  return {
    props: {
      statusCode,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  }
}

function Page({ statusCode, blog }: GetServerSideProps): React.ReactElement {
  switch (statusCode) {
    case 404:
      return <Error4xx />
    case 200:
      if (blog) {
        return <Blog blog={blog} />
      }
    default:
      return <Error5xx />
  }
}

export default Page
